import { useDebounce } from 'use-debounce';

import { useFeatureDetails } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';

/**
 * Recommends learning library courses for the goal being written. Only queries once the
 * workspace actually has visible courses (i.e. an active learning connector) and the
 * title or description is substantial enough to recommend on.
 * @param input The goal draft: title and plain-text description.
 * @param input.title Current goal title text.
 * @param input.description Plain-text goal description, if any.
 */
export default function useGoalLearningRecommendations(input: {
	title: string;
	description?: string;
}) {
	const [title] = useDebounce(input.title.trim(), 800);
	const [description] = useDebounce(input.description?.trim() || undefined, 800);

	const goodHabitzFeature = useFeatureDetails(graphql.FeatureName.Goodhabitz);

	const library = graphql.useLearningCoursesQuery({
		variables: { first: 1 },
		fetchPolicy: 'cache-first',
		skip: goodHabitzFeature.loading || !goodHabitzFeature.available,
	});

	const hasCourses =
		goodHabitzFeature.available && (library.data?.learningCourses.totalCount ?? 0) > 0;
	const canQuery = hasCourses && (title.length >= 3 || (description?.length ?? 0) >= 3);

	const recommendations = graphql.useGoalLearningRecommendationsQuery({
		variables: {
			input: {
				title,
				description,
			},
		},
		skip: !canQuery,
		fetchPolicy: 'cache-first',
	});

	return {
		available: hasCourses,
		loading: recommendations.loading,
		recommendations: canQuery
			? (recommendations.data?.goalLearningRecommendations.recommendations ?? [])
			: [],
	};
}
